import React, { useState, useEffect, useCallback } from 'react';

import { Form, InputNumber, Switch, Select, Row, Col, Divider, Tooltip, message } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import {
  TIME_SLOTS,
  PLATFORMS_WITH_SHIPPING_MAPPING,
  PLATFORMS_WITHOUT_CUSTOM_SHIPPING_CODE
} from '@/constants/ecAccountPlatforms';

import CoupangShippingSettings from './CoupangShippingSettings';
import EcShippingTypeMappings from './EcShippingTypeMappings';

const SettingsPanel = ({
  form,
  connectionStatus,
  moduleName,
  shippingTypes,
  ecAccount,
  ecAccountService,
}) => {
  const { t } = useTranslation();

  const isLocked = connectionStatus !== 'connected';
  const supportsShippingMapping = moduleName && PLATFORMS_WITH_SHIPPING_MAPPING.includes(moduleName);
  const isCoupang = moduleName === 'Coupang';

  const [returnCenters, setReturnCenters] = useState({ seven: [], family: [] });
  const [outboundPlaces, setOutboundPlaces] = useState([]);
  const [shippingInfoLoading, setShippingInfoLoading] = useState(false);

  const toSelectOptions = useCallback((items) => (
    (items || []).map(c => ({
      value: c.code,
      label: `${c.display_text} (${c.code})${!c.usable ? ' [不可用]' : ''}`,
      disabled: !c.usable
    }))
  ), []);

  const loadCoupangShippingInfo = useCallback(async () => {
    if (!ecAccount?.id || !ecAccountService?.getCoupangShippingInfo) return;
    setShippingInfoLoading(true);
    try {
      const data = await ecAccountService.getCoupangShippingInfo(ecAccount.id);
      if (data.success) {
        const payload = data.data || {};
        const centers = payload.return_centers || {};
        setReturnCenters({
          seven: toSelectOptions(centers['TWL_711']),
          family: toSelectOptions(centers['TWL_FM'])
        });
        setOutboundPlaces(toSelectOptions(payload.outbound_places));
      } else {
        message.error(data.message || t('models.ec_account.platform_settings.coupang.load_failed'));
      }
    } catch (error) {
      console.error('Failed to load Coupang shipping info:', error);
      message.error(t('models.ec_account.platform_settings.coupang.load_failed'));
    } finally {
      setShippingInfoLoading(false);
    }
  }, [ecAccount?.id, ecAccountService, toSelectOptions, t]);

  // 連線成功後自動載入一次清單，方便使用者直接從下拉選擇
  useEffect(() => {
    if (isCoupang && !isLocked && ecAccount?.id) {
      loadCoupangShippingInfo();
    }
  }, [isCoupang, isLocked, ecAccount?.id, loadCoupangShippingInfo]);

  if (isLocked) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: 300, background: '#f5f5f5', borderRadius: 8
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🔗</div>
          <div style={{ fontWeight: 500, color: '#666' }}>{t('models.ec_account.settings_panel_locked')}</div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>{t('models.ec_account.settings_panel_locked_description')}</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
        {t('models.ec_account.settings_panel_title')}
      </div>

      <Divider style={{ marginTop: 0 }} />

      {/* 抓取訂單設定 */}

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="enable_fetch_orders"
            label={t('models.ec_account.enable_fetch_orders')}
            valuePropName="checked"
          >
            <Switch
              checkedChildren={t('enums.ec_account.status.active')}
              unCheckedChildren={t('enums.ec_account.status.inactive')}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            name="fetch_schedule"
            label={t('models.ec_account.fetch_schedule')}
            dependencies={['enable_fetch_orders']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (getFieldValue('enable_fetch_orders') && (!value || value.length === 0)) {
                    return Promise.reject(new Error(t('models.ec_account.fetch_schedule_required')));
                  }
                  return Promise.resolve();
                }
              })
            ]}
          >
            <Select
              mode="multiple"
              maxCount={3}
              placeholder={t('common.placeholder.please_select')}
              options={TIME_SLOTS}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* 庫存同步設定 */}
      <Divider />
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="stock_push_enabled"
            label={t('models.ec_account.stock_push_enabled')}
            valuePropName="checked"
          >
            <Switch
              checkedChildren={t('enums.ec_account.status.active')}
              unCheckedChildren={t('enums.ec_account.status.inactive')}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="stock_percentage"
            label={
              <span>
                {t('models.ec_account.stock_percentage')}&nbsp;
                <Tooltip title={t('models.ec_account.stock_percentage_tooltip')}>
                  <InfoCircleOutlined style={{ color: '#aaa' }} />
                </Tooltip>
              </span>
            }
            rules={[
              {
                type: 'number',
                min: 0,
                max: 100,
                message: t('common.validation.range', { min: 0, max: 100, defaultValue: '請輸入 0–100 的數值' })
              }
            ]}
          >
            <InputNumber
              min={0}
              max={100}
              step={1}
              addonAfter="%"
              style={{ width: '100%' }}
              placeholder="100"
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Coupang 退貨地 / 發貨地設定 */}
      {isCoupang && (
        <>
          <Divider />
          <CoupangShippingSettings
            form={form}
            returnCenters={returnCenters}
            outboundPlaces={outboundPlaces}
            loading={shippingInfoLoading}
            onReload={loadCoupangShippingInfo}
          />
        </>
      )}

      {/* 物流代碼對應 - 僅支援的平台顯示 */}
      {supportsShippingMapping && (
        <>
          <Divider />
          <Form.Item
            name="shipping_type_mappings"
            label={t('models.ec_account.shipping_mapping_settings')}
            rules={[
              {
                validator: (_, value) => {
                  if (value && value.some(item => !item.shipping_type_id)) {
                    return Promise.reject(new Error(t('models.ec_account.shipping_mapping_required', { defaultValue: '所有物流對應皆須選擇倉庫端物流' })));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <EcShippingTypeMappings
              shippingTypes={shippingTypes}
              moduleName={moduleName}
              allowCustomCode={!PLATFORMS_WITHOUT_CUSTOM_SHIPPING_CODE.includes(moduleName)}
            />
          </Form.Item>
        </>
      )}
    </div>
  );
};

export default SettingsPanel;
